<script setup lang="ts">
import BaseIcon, { type IconName } from './BaseIcon.vue';

type CircleButtonVariant = 'primary' | 'secondary';

const props = withDefaults(defineProps<{
  icon: IconName;
  variant?: CircleButtonVariant;
  size?: number;
  iconSize?: number;
  disabled?: boolean;
  ariaLabel?: string;
}>(), {
  variant: 'primary',
  size: 40,
  iconSize: 20,
  disabled: false
});

const emit = defineEmits<{
  click: [event: MouseEvent]
}>();

function handleClick(event: MouseEvent) {
  if (!props.disabled) {
    emit('click', event);
  }
}
</script>

<template>
  <button
    class="base-circle-button"
    :class="[
      `base-circle-button--${variant}`,
      { 'base-circle-button--disabled': disabled }
    ]"
    :style="{ width: `${size}px`, height: `${size}px` }"
    :disabled="disabled"
    :aria-label="ariaLabel"
    @click="handleClick"
  >
    <BaseIcon :name="icon" :size="iconSize" />
  </button>
</template>

<style scoped>
.base-circle-button {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 1px solid var(--color-primary);
  border-radius: 50%;
  cursor: pointer;
  flex-shrink: 0;
  transition: all 0.2s ease;
}

.base-circle-button--primary {
  background-color: var(--color-primary);
  color: var(--color-text-light);
}

.base-circle-button--secondary {
  background-color: transparent;
  color: var(--color-text-dark);
  border-color: var(--color-border);
}

.base-circle-button--disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.base-circle-button:hover:not(.base-circle-button--disabled) {
  opacity: 0.9;
}
</style>
